import {
  REMINDER_COOLDOWN_MS,
  ACCESS_LEVEL_LABEL,
  formatDate,
  daysSince,
  timeAgo,
  displayStatus,
  agingTone,
} from './helpers';
import { FiBell, FiClock, FiCheck, FiX, FiSlash } from 'react-icons/fi';
import { TbArrowUpRight } from 'react-icons/tb';
import Button from '../common/Button';

/**
 * AccessRequestCard — one company-access request the professional initiated,
 * rendered as a card for the unified dashboard list. Tagged "Requested" and
 * carries the rate-limited Send Reminder action while still pending.
 */
export default function AccessRequestCard({ req, onRemind, reminding }) {
  const status = displayStatus(req);
  const company = req.company;
  const owner = req.owner;
  const waiting = daysSince(req.requestedAt);

  const remindedAgo = req.lastRemindedAt
    ? Date.now() - new Date(req.lastRemindedAt).getTime()
    : Infinity;
  const inCooldown = remindedAgo < REMINDER_COOLDOWN_MS;
  const cooldownHoursLeft = inCooldown
    ? Math.ceil((REMINDER_COOLDOWN_MS - remindedAgo) / (60 * 60 * 1000))
    : 0;

  return (
    <div
      className="bg-white rounded-[12px] border p-4 transition-shadow hover:shadow-md"
      style={{ borderColor: '#ddd6c6' }}
    >
      {/* Header: source tag + status */}
      <div className="flex items-start justify-between gap-3">
        <span
          className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.5px] px-2 py-[3px] rounded"
          style={{ background: '#b8973a22', color: '#9a7b28' }}
        >
          <TbArrowUpRight size={10} aria-hidden="true" /> Requested by you
        </span>
        <span
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold"
          style={{ background: status.bg, color: status.color }}
        >
          <span className="w-[6px] h-[6px] rounded-full" style={{ background: status.dot }} />
          {status.label}
        </span>
      </div>

      {/* Company */}
      <div className="mt-2">
        <div className="text-[14px] font-bold leading-tight truncate" style={{ color: '#08111f' }}>
          {company?.name || 'Company unavailable'}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {company?.cin && (
            <span
              className="text-[10.5px]"
              style={{ color: '#7a7060', fontFamily: "'JetBrains Mono', monospace" }}
            >
              {company.cin}
            </span>
          )}
          {company?.type && (
            <span className="text-[10.5px]" style={{ color: '#a39a88' }}>
              · {company.type}
            </span>
          )}
        </div>
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t" style={{ borderColor: '#f0ebdf' }}>
        <div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
            Requested from
          </div>
          <div className="text-[11.5px] font-semibold mt-0.5 truncate" style={{ color: '#08111f' }}>
            {owner?.fullName || 'Company owner'}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
            Access level
          </div>
          <div className="text-[11.5px] font-semibold mt-0.5" style={{ color: '#08111f' }}>
            {ACCESS_LEVEL_LABEL[req.accessLevel] || req.accessLevel}
          </div>
        </div>
        <div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.4px]" style={{ color: '#a39a88' }}>
            Requested on
          </div>
          <div className="text-[11.5px] font-semibold mt-0.5" style={{ color: '#08111f' }}>
            {formatDate(req.requestedAt)}
          </div>
        </div>
      </div>

      {/* Status-specific footer */}
      {status.key === 'pending' ? (
        <div
          className="mt-3 pt-3 border-t flex items-center justify-between gap-3 flex-wrap"
          style={{ borderColor: '#f0ebdf' }}
        >
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: agingTone(waiting) }}>
              <FiClock size={12} aria-hidden="true" /> {waiting === 0 ? 'Requested today' : `Waiting ${waiting} day${waiting === 1 ? '' : 's'}`}
            </div>
            <div className="text-[10.5px] mt-0.5" style={{ color: '#a39a88' }}>
              {req.reminderCount > 0
                ? `Reminded ${req.reminderCount}× · last ${timeAgo(req.lastRemindedAt)}`
                : 'No reminder sent yet'}
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            className="shrink-0"
            disabled={reminding || inCooldown || !company?._id}
            onClick={() => onRemind(req)}
            title={inCooldown ? `You can remind again in about ${cooldownHoursLeft}h` : 'Nudge the company owner'}
            leftIcon={!reminding && !inCooldown ? <FiBell size={15} /> : undefined}
          >
            {reminding
              ? 'Sending…'
              : inCooldown
                ? `Reminded · ${cooldownHoursLeft}h`
                : 'Send reminder'}
          </Button>
        </div>
      ) : status.key === 'active' ? (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: '#f0ebdf' }}>
          <div className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: '#4a6b3f' }}>
            <FiCheck size={13} aria-hidden="true" /> Access granted{req.grantedAt ? ` on ${formatDate(req.grantedAt)}` : ''}
          </div>
          {req.grantedBy?.fullName && (
            <div className="text-[10.5px] mt-0.5" style={{ color: '#a39a88' }}>
              Approved by {req.grantedBy.fullName}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: '#f0ebdf' }}>
          <div className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: status.color }}>
            {status.key === 'revoked' ? (
              <><FiSlash size={12} aria-hidden="true" /> Access revoked{req.revokedAt ? ` on ${formatDate(req.revokedAt)}` : ''}</>
            ) : (
              <><FiX size={13} aria-hidden="true" /> Request declined{req.revokedAt ? ` on ${formatDate(req.revokedAt)}` : ''}</>
            )}
          </div>
          <div className="text-[10.5px] mt-0.5" style={{ color: '#a39a88' }}>
            You can request access again from the Companies page.
          </div>
        </div>
      )}
    </div>
  );
}
